import Phaser from 'phaser';
import { Dir } from '../../sim/direction';
import { FX_FRAME, FX_KEY } from '../fx/Effects';
import { TILES_KEY } from '../render/placeholderArt';
import { frameIndex } from '../render/tileFrames';
import { saves } from '../records';
import { CELL_W } from '../ui/pixelFont';
import { label, UI } from '../ui/text';
import { wipeTo } from './WipeScene';
import { pickQuip } from '../ui/quips';
import { ownMowerTexture } from '../render/cosmeticArt';

const TILE = 16;
const LOGO = 'lawnmower.';
const LOGO_SCALE = 3;
/** The mower's pass across the screen, the pause on the finished logo, and the tagline delay. */
const PASS_MS = 1300;
const HOLD_MS = 1100;

/**
 * The title, once per launch: an overgrown lawn, a mower cuts a stripe across it and the
 * "lawnmower." logo appears in the cut, then a mowing wipe to the menu. A tap skips it; with reduced
 * motion the logo simply shows.
 */
export class TitleScene extends Phaser.Scene {
  private leaving = false;

  constructor() {
    super('Title');
  }

  create(): void {
    this.leaving = false;
    const { width, height } = this.scale.gameSize;
    const still = saves().save.settings.reducedShake;
    const rows = Math.ceil(height / TILE);
    const cols = Math.ceil(width / TILE);
    const stripeRow = Math.max(2, Math.round(rows * 0.4) - 1);
    const stripeTop = stripeRow * TILE;
    const STRIPE_ROWS = 4; // three for the logo, one for the tagline
    const stripeH = TILE * STRIPE_ROWS;

    // The lawn: unmowed grass everywhere (the three tuft layouts, mixed like a real lawn), with
    // the stripe's mowed tiles hidden until the mower passes.
    const grass = (['grass', 'grass2', 'grass3'] as const).map((f) => frameIndex(f));
    const cut: Phaser.GameObjects.Image[] = [];
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const inStripe = y >= stripeRow && y < stripeRow + STRIPE_ROWS;
        this.add.image(x * TILE, y * TILE, TILES_KEY, grass[(x * 2 + y) % 3]).setOrigin(0);
        if (inStripe) {
          const mowed = this.add
            .image(x * TILE, y * TILE, TILES_KEY, frameIndex('mowedE'))
            .setOrigin(0)
            .setVisible(still);
          cut.push(mowed);
        }
      }
    }

    // The logo, one letter at a time so each appears as the mower passes it.
    const letterW = CELL_W * LOGO_SCALE;
    // The font is monospaced; the narrow "l" is kerned one font pixel tighter.
    const advance = (ch: string) => letterW - (ch === 'l' ? LOGO_SCALE : 0);
    const logoW = [...LOGO].reduce((w, ch) => w + advance(ch), 0);
    const logoY = stripeTop + Math.round((TILE * 3 - 7 * LOGO_SCALE) / 2);
    let lx = Math.round((width - logoW) / 2);
    const letters = [...LOGO].map((ch) => {
      const l = label(this, lx, logoY, ch, LOGO_SCALE, ch === '.' ? UI.accent : UI.text)
        .setDropShadow(2, 2, UI.shadow, 0.9)
        .setVisible(still);
      lx += advance(ch);
      return l;
    });
    const tagline = label(this, width / 2, stripeTop + TILE * 3 + 4, pickQuip(), 1, UI.text)
      .setOrigin(0.5, 0)
      .setAlpha(still ? 1 : 0);

    // Tap anywhere to skip.
    this.input.once('pointerup', () => this.leave());
    this.input.keyboard?.once('keydown', () => this.leave());

    if (still) {
      this.time.delayedCall(HOLD_MS, () => this.leave());
      return;
    }

    // The mower's pass, throwing clippings.
    const mower = this.add
      .image(
        -24,
        stripeTop + stripeH / 2,
        ownMowerTexture(this, saves().save.selectedMower, saves().save),
        Dir.E,
      )
      .setScale(2);
    const spray = this.add.particles(0, 0, FX_KEY, {
      frame: [FX_FRAME.clipLight, FX_FRAME.clipDark],
      speed: { min: 30, max: 70 },
      angle: { min: 60, max: 120 },
      lifespan: { min: 250, max: 450 },
      alpha: { start: 1, end: 0 },
      rotate: { min: 0, max: 360 },
      frequency: 30,
      quantity: 2,
      emitting: false,
    });
    const travel = { x: -24 };
    this.tweens.add({
      targets: travel,
      x: width + 28,
      duration: PASS_MS,
      ease: 'Sine.easeInOut',
      delay: 250,
      onStart: () => spray.start(),
      onUpdate: () => {
        const x = Math.round(travel.x);
        mower.setX(x);
        spray.setPosition(x - 10, stripeTop + stripeH - 6);
        const edge = x - 12;
        for (const t of cut) if (!t.visible && t.x + TILE / 2 < edge) t.setVisible(true);
        letters.forEach((l) => {
          if (!l.visible && l.x + letterW / 2 < edge) this.popLetter(l);
        });
      },
      onComplete: () => {
        spray.stop();
        for (const t of cut) t.setVisible(true);
        letters.forEach((l) => l.visible || this.popLetter(l));
        this.tweens.add({ targets: tagline, alpha: 1, duration: 400 });
        this.time.delayedCall(HOLD_MS, () => this.leave());
      },
    });
  }

  /** A letter appears in the cut with a little hop. */
  private popLetter(l: Phaser.GameObjects.BitmapText): void {
    const y = l.y;
    l.setVisible(true).setY(y - 4);
    this.tweens.add({ targets: l, y, duration: 260, ease: 'Bounce.easeOut' });
  }

  private leave(): void {
    if (this.leaving) return;
    this.leaving = true;
    wipeTo(this, 'Menu');
  }
}
